import { useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Sidebar } from "primereact/sidebar";

import { FiSearch, FiX, FiEdit2, FiTrash2, FiAlertTriangle, FiUserPlus } from "react-icons/fi";
import { MdOutlineArrowLeft, MdOutlineArrowRight, MdWorkspacePremium } from "react-icons/md";

import VendorAvatar from "./VendorAvatar";
import AddPaidVendors from "./AddPaidVendors";
import EditPaidVendor from "./EditPaidVendor";
import { usePaidVendors, useRemovePaidVendors } from "../../hooks/paid-vendors";
import { useDebounce } from "../../hooks/useDebounce";
import { useSignedUrlMap } from "../../hooks/useSignedUrlMap";

// `field:direction` keeps the whole sort in one <Dropdown> value.
const SORT_OPTIONS = [
  { label: "Recently added", value: "createdAt:desc" },
  { label: "Oldest first", value: "createdAt:asc" },
  { label: "Name (A - Z)", value: "name:asc" },
  { label: "Name (Z - A)", value: "name:desc" },
  { label: "Email (A - Z)", value: "email:asc" },
];

const PAGE_SIZES = [10, 20, 30, 50];

const StatCard = ({ icon, label, value, tone }) => (
  <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
    <span className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${tone}`}>
      {icon}
    </span>
    <div>
      <p className="text-xl font-semibold text-gray-800 leading-none">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  </div>
);

const EmptyState = ({ isSearching, canManage, onAdd }) => (
  <div className="bg-white rounded-xl border border-dashed border-gray-200 py-16 px-6 text-center">
    <span className="mx-auto w-14 h-14 rounded-full bg-primary8 text-primary flex items-center justify-center">
      <MdWorkspacePremium className="text-2xl" />
    </span>
    <h3 className="mt-4 text-base font-semibold text-gray-800">
      {isSearching ? "No paid vendors match that search" : "No paid vendors yet"}
    </h3>
    <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
      {isSearching
        ? "Try a different name, email or company — or clear the search to see the full list."
        : "Vendors on this list can see customer contact details on the enquiries they have replied to."}
    </p>
    {!isSearching && canManage && (
      <button
        type="button"
        onClick={onAdd}
        className="mt-5 inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:brightness-95"
      >
        <FiUserPlus /> Add paid vendors
      </button>
    )}
  </div>
);

/** One dialog for both flows: a single row's action and a bulk selection. */
const ConfirmRemoveDialog = ({ targets, isPending, onCancel, onConfirm }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
          <FiAlertTriangle className="text-lg" />
        </span>
        <h2 className="text-lg font-semibold text-gray-800">
          {targets.length === 1 ? "Remove paid vendor" : `Remove ${targets.length} paid vendors`}
        </h2>
      </div>

      <p className="text-sm text-gray-600 mt-3">
        {targets.length === 1 ? (
          <>
            <span className="font-semibold">{targets[0].name || targets[0].email}</span> will
            no longer see customer contact details on their enquiries.
          </>
        ) : (
          "The selected vendors will no longer see customer contact details on their enquiries."
        )}
      </p>

      {targets.length > 1 && (
        <ul className="mt-3 max-h-40 overflow-y-auto text-xs text-gray-600 bg-gray-50 rounded-lg p-3 space-y-1">
          {targets.map((target) => (
            <li key={target._id} className="truncate">
              {target.name || "Unnamed vendor"} — {target.email}
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-gray-400 mt-3">
        They keep their account and listings; only contact-detail access is revoked. You can
        add them back at any time.
      </p>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isPending}
          className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
        >
          {isPending ? "Removing..." : "Remove"}
        </button>
      </div>
    </div>
  </div>
);

const PaidVendors = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState(SORT_OPTIONS[0].value);
  const [selectedRows, setSelectedRows] = useState([]);

  const [panel, setPanel] = useState(null); // { mode: 'ADD' } | { mode: 'EDIT', row }
  const [removalTargets, setRemovalTargets] = useState(null);

  const debouncedSearch = useDebounce(searchTerm);
  const [sortField, sortOrder] = sort.split(":");

  // USER-role admins get the read-only view, matching the other admin screens.
  const canManage = localStorage.getItem("role") !== "USER";

  const { data, isLoading, isFetching, error } = usePaidVendors({
    page: currentPage,
    limit: itemsPerPage,
    search: debouncedSearch,
    sortField,
    sortOrder,
  });
  const { mutate: removePaidVendors, isPending: isRemoving } = useRemovePaidVendors();

  const rows = data?.paidVendors ?? [];
  const pagination = data?.pagination;
  const stats = data?.stats;
  const totalPages = pagination?.totalPages ?? 1;

  const avatars = useSignedUrlMap(rows.map((row) => row.profilePic));

  // Anything that renumbers the results starts over at page one. Done in the
  // handlers rather than an effect so the query never fires with a page index
  // that belongs to the previous result set.
  const resetToFirstPage = () => {
    setCurrentPage(1);
    setSelectedRows([]);
  };

  // Rows the admin can no longer see must not stay selected — a hidden
  // selection makes the bulk action feel arbitrary.
  const goToPage = (page) => {
    setCurrentPage(page);
    setSelectedRows([]);
  };

  const confirmRemoval = () => {
    // Clearing out the last page would otherwise leave the admin looking at an
    // empty table that reads like the whole list is empty.
    const emptiesThePage = removalTargets.length >= rows.length && currentPage > 1;

    removePaidVendors(
      removalTargets.map((target) => target.vendorId),
      {
        onSuccess: (result) => {
          toast.success(result?.message || "Removed from the paid list");
          if (emptiesThePage) setCurrentPage(currentPage - 1);
          setSelectedRows([]);
          setRemovalTargets(null);
        },
        onError: (removeError) => {
          toast.error(removeError.message);
          setRemovalTargets(null);
        },
      }
    );
  };

  const vendorTemplate = (row) => (
    <div className="flex items-center gap-3 py-0.5">
      <VendorAvatar
        src={avatars.get(row.profilePic)}
        name={row.name}
        email={row.email}
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate max-w-[220px]">
          {row.name || "Unnamed vendor"}
        </p>
        <p className="text-xs text-gray-500 truncate max-w-[220px]">
          {row.organization || "No company on file"}
        </p>
      </div>
    </div>
  );

  const contactTemplate = (row) => (
    <div className="min-w-0">
      <a
        href={`mailto:${row.email}`}
        className="text-sm text-gray-700 hover:text-primary hover:underline truncate block max-w-[240px]"
        title={row.email}
      >
        {row.email || "--"}
      </a>
      <p className="text-xs text-gray-400">{row.phone || "No phone on file"}</p>
    </div>
  );

  const statusTemplate = (row) =>
    row.vendorExists ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-2 py-0.5 text-[11px] font-medium">
        <MdWorkspacePremium className="text-xs" /> Paid
      </span>
    ) : (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 px-2 py-0.5 text-[11px] font-medium"
        title="The vendor account behind this entry has been deleted"
      >
        <FiAlertTriangle className="text-xs" /> Vendor deleted
      </span>
    );

  const addedTemplate = (row) => (
    <div>
      <p className="text-sm text-gray-700">{moment(row.createdAt).format("DD MMM YYYY")}</p>
      <p className="text-xs text-gray-400">{moment(row.createdAt).fromNow()}</p>
    </div>
  );

  const actionsTemplate = (row) => (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setPanel({ mode: "EDIT", row })}
        aria-label="Edit paid vendor"
        title="Edit"
        className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
      >
        <FiEdit2 />
      </button>
      <button
        type="button"
        onClick={() => setRemovalTargets([row])}
        aria-label="Remove paid vendor"
        title="Remove from paid list"
        className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600"
      >
        <FiTrash2 />
      </button>
    </div>
  );

  const showEmptyState = !isLoading && rows.length === 0;

  return (
    <div className="px-5 pb-16 pt-4 flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Paid Vendors</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Vendors allowed to see customer contact details on enquiries they have replied to.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setPanel({ mode: "ADD" })}
            className="inline-flex items-center justify-center gap-2 bg-primary text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:brightness-95"
          >
            <FiUserPlus /> Add Paid Vendors
          </button>
        )}
      </div>

      {/* Counters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          icon={<MdWorkspacePremium />}
          label="Vendors on the paid list"
          value={stats?.total ?? "--"}
          tone="bg-primary8 text-primary"
        />
        <StatCard
          icon={<FiUserPlus />}
          label={`Added in the last ${stats?.recentlyAddedDays ?? 30} days`}
          value={stats?.recentlyAdded ?? "--"}
          tone="bg-emerald-50 text-emerald-600"
        />
        <StatCard
          icon={<FiAlertTriangle />}
          label="Entries with a deleted vendor"
          value={stats?.orphaned ?? "--"}
          tone={stats?.orphaned ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-gray-400"}
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              resetToFirstPage();
            }}
            placeholder="Search paid vendors by name, email or company"
            className="w-full bg-white border border-gray-200 rounded-lg py-2.5 pl-9 pr-9 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                resetToFirstPage();
              }}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <FiX />
            </button>
          )}
        </div>

        <Dropdown
          value={sort}
          options={SORT_OPTIONS}
          onChange={(event) => {
            setSort(event.value);
            resetToFirstPage();
          }}
          className="w-full lg:w-56 bg-white border border-gray-200 rounded-lg"
          panelClassName="text-sm"
        />
      </div>

      {/* Bulk actions */}
      {canManage && selectedRows.length > 0 && (
        <div className="flex items-center justify-between gap-3 bg-neutralBlack text-white rounded-xl px-4 py-3">
          <span className="text-sm">
            {selectedRows.length} vendor{selectedRows.length === 1 ? "" : "s"} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedRows([])}
              className="px-3 py-1.5 text-sm rounded-lg hover:bg-white/10"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setRemovalTargets(selectedRows)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-red-600 hover:bg-red-700"
            >
              <FiTrash2 /> Remove from paid list
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {error.message}
        </div>
      )}

      {showEmptyState ? (
        <EmptyState
          isSearching={Boolean(debouncedSearch)}
          canManage={canManage}
          onAdd={() => setPanel({ mode: "ADD" })}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <DataTable
            value={rows}
            dataKey="_id"
            loading={isLoading || isFetching}
            selection={selectedRows}
            onSelectionChange={(event) => setSelectedRows(event.value)}
            responsiveLayout="scroll"
            className="text-sm"
          >
            {canManage && (
              <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
            )}
            <Column header="Vendor" body={vendorTemplate} className="py-3" />
            <Column header="Contact" body={contactTemplate} className="py-3" />
            <Column
              header="Access"
              body={statusTemplate}
              className="py-3"
              style={{ width: "10rem" }}
            />
            <Column
              header="Added"
              body={addedTemplate}
              className="py-3"
              style={{ width: "11rem" }}
            />
            {canManage && (
              <Column
                header="Actions"
                body={actionsTemplate}
                className="py-3"
                style={{ width: "7rem" }}
              />
            )}
          </DataTable>
        </div>
      )}

      {/* Pagination */}
      {!showEmptyState && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            Showing {rows.length} of {pagination?.totalCount ?? 0}
            {debouncedSearch ? " matching" : ""} vendor
            {(pagination?.totalCount ?? 0) === 1 ? "" : "s"}
          </p>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className="disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <MdOutlineArrowLeft className="text-primary text-2xl" />
              </button>
              <span className="text-xs text-gray-600">
                Page <span className="font-semibold">{currentPage}</span> of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
                className="disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <MdOutlineArrowRight className="text-primary text-2xl" />
              </button>
            </div>

            <select
              value={itemsPerPage}
              onChange={(event) => {
                setItemsPerPage(Number(event.target.value));
                resetToFirstPage();
              }}
              aria-label="Rows per page"
              className="py-2 px-3 bg-white border border-gray-200 rounded-lg text-sm"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {removalTargets && (
        <ConfirmRemoveDialog
          targets={removalTargets}
          isPending={isRemoving}
          onCancel={() => setRemovalTargets(null)}
          onConfirm={confirmRemoval}
        />
      )}

      <Sidebar
        visible={Boolean(panel)}
        position="right"
        onHide={() => setPanel(null)}
        className="w-full sm:w-[520px] md:w-[560px]"
        dismissable
        modal
      >
        {panel?.mode === "ADD" && (
          <AddPaidVendors
            onAdded={() => setPanel(null)}
            onCancel={() => setPanel(null)}
          />
        )}
        {panel?.mode === "EDIT" && (
          <EditPaidVendor
            paidVendor={panel.row}
            onSaved={() => setPanel(null)}
            onCancel={() => setPanel(null)}
          />
        )}
      </Sidebar>
    </div>
  );
};

export default PaidVendors;
